import { type PipeTransform } from '@angular/core';
import { type AbstractControl, type ValidationErrors } from '@angular/forms';

import { isQuestionPercentage } from '../questions/question.helpers';

import { type QuestionValidator } from './interfaces';

export type AmountErrorType = '>' | '<' | '<=';
export type AmountErrorFormatter = (
  amount: number,
  type: AmountErrorType,
  pipe?: PipeTransform,
) => string;

export function amountLimitValidators(
  errorFormatter: AmountErrorFormatter,
  pipe?: PipeTransform,
): QuestionValidator<number> {
  return (
    control: AbstractControl,
    value,
    elements,
    self,
  ): ValidationErrors | null => {
    let error = {};

    if (isQuestionPercentage(self)) {
      value = value * 100;
    }

    const minError = amountMinValidator(amount =>
      errorFormatter(amount, '<', pipe),
    )(control, value, elements, self);
    const maxError = amountMaxValidator(amount =>
      errorFormatter(amount, '>', pipe),
    )(control, value, elements, self);

    const strictlyPositiveError = amountStrictlyPositiveValidator(() =>
      errorFormatter(0, '<=', pipe),
    )(control, value, elements, self);

    if (minError) {
      error = minError;
    }

    if (maxError) {
      error = {
        ...error,
        ...maxError,
      };
    }

    if (strictlyPositiveError) {
      error = {
        ...error,
        ...strictlyPositiveError,
      };
    }

    return Object.keys(error).length ? error : null;
  };
}

export function amountMinValidator(
  errorFormatter: (amount: number) => string,
): QuestionValidator<number> {
  return (
    _: AbstractControl,
    value: number,
    __,
    self,
  ): ValidationErrors | null => {
    return typeof self.min === 'number' &&
      typeof value === 'number' &&
      value < self.min
      ? {
          [errorFormatter(self.min)]: true,
        }
      : null;
  };
}

export function amountMaxValidator(
  errorFormatter: (amount: number) => string,
): QuestionValidator<number> {
  return (
    _: AbstractControl,
    value: number,
    __,
    self,
  ): ValidationErrors | null => {
    return typeof self.max === 'number' &&
      typeof value === 'number' &&
      value > self.max
      ? {
          [errorFormatter(self.max)]: true,
        }
      : null;
  };
}

/**
 * Return an error, with the errorMessageKey set, when value is not strictly positive.
 */
export function amountStrictlyPositiveValidator(
  errorFormatter: (amount: number) => string,
): QuestionValidator<number> {
  return (
    _: AbstractControl,
    value: number,
    __,
    self,
  ): ValidationErrors | null => {
    return typeof self.strictlyPositive === 'boolean' &&
      typeof value === 'number' &&
      value <= 0
      ? {
          [errorFormatter(0)]: true,
        }
      : null;
  };
}
